<template>
  <div ref="logsContent" class="w-full flex justify-center" 
  :style="{ '--details-anim-dur': `${DETAILS_ANIM_DUR}ms`, '--table-max-width': `${TABLE_MAX_WIDTH}px` }">
    <!-- LOGS FILTER TOOLBAR (portals to parent) -->
    <portal to="api-filter-toolbar">
      <LogsFilterToolbar 
        :queryFields.sync="queryFields"
        :selectedDateRange.sync="selectedDateRange"
        :sortDirection.sync="sortDirection"
        :refetchingLogs="loadingLogsRefetch"
        @clearQueryFields="clearQueryFields"
        @refresh="fetchLogs()"
      />
    </portal>
    <!-- LOGS TABLE -->
    <div ref="logsTable" class="scroll-container overflow-x-auto z-10 bg-white border-l border-t border-r border-border-normal"
    :class="[detailsMaximized ? 'anim-shrink' : 'anim-expand', { 'border-b': showTableBottomBorder }]">
      <!-- Table -->
      <table>
        <thead>
          <tr class="header-row">
            <th class="flex items-center gap-1.5">
              <BaseText type="heading" size="overline" class="text-text-subdued">
                TIME
              </BaseText>
              <BaseText type="heading" size="overline" class="text-text-disabled">
                (LOCAL)
              </BaseText>
            </th>
            <th><BaseText type="heading" size="overline" class="text-text-subdued">
              API
            </BaseText></th>
            <th style="min-width: 90px"><BaseText type="heading" size="overline" class="text-text-subdued">
              METHOD
            </BaseText></th>
            <th style="min-width: 201px"><BaseText type="heading" size="overline" class="text-text-subdued">
              ENDPOINT
            </BaseText></th>
            <th style="min-width: 104px"><BaseText type="heading" size="overline" class="text-text-subdued">
              RESPONSE
            </BaseText></th>
            <th style="min-width: 129px"><BaseText type="heading" size="overline" class="text-text-subdued">
              IP ADDRESS
            </BaseText></th>
            <th><BaseText type="heading" size="overline" class="text-text-subdued">
              LOCATION
            </BaseText></th>
            <th><BaseText type="heading" size="overline" class="text-text-subdued">
              LATENCY
            </BaseText></th>
            <th><BaseText type="heading" size="overline" class="text-text-subdued">
              CREDITS USED
            </BaseText></th>
            <th />
          </tr>
        </thead>
        <!-- Init Loading State -->
        <div v-if="loadingLogs" style="height: 120px;" class="w-full">
          <div class="absolute top-9 left-0 right-0 bottom-0 flex items-center justify-center">
            <BaseLoadingLogo />
          </div>
        </div>
        <!-- Empty State -->
        <div v-if="!loadingLogs && !logs?.length" class="w-full fade-in" style="height: 160px;">
          <div class="absolute top-14 left-0 right-0 flex flex-col items-center justify-center">
            <NoSearchResultsIcon light-mode />
            <BaseText type="label" size="sm" class="text-text-muted">
              No logs found
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal mt-1">
              Try reducing your filters
            </BaseText>
          </div>
        </div>
        <!-- Log Rows -->
        <tbody v-if="!loadingLogs && logs?.length" class="transition-opacity duration-200" :class="loadingLogsRefetch ? 'opacity-50' : 'opacity-100'">
          <tr v-for="log in formattedLogs" :key="`log-row-${log.trace_id}`"
          class="log-row group fade-in" :class="{ 'active': isLogRowActive(log), 'no-trace-data': !log.has_trace_data }"
          @click="selectLog(log)">
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.timestamp }}
            </BaseText></td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.product }}
            </BaseText></td>
            <td>
              <div class="colored-label w-max" :class="log.method">
                {{ log.method }}
              </div>
            </td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.endpoint }}
            </BaseText></td>
            <td>
              <div class="colored-label w-max" 
              :class="log.response_status >= 200 && log.response_status < 300 ? 'success' : 'error'">
                {{ log.response_status }}
              </div>
            </td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.ip_address }}
            </BaseText></td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.country }}
            </BaseText></td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.latency_ms }}
            </BaseText></td>
            <td><BaseText type="body" size="small" class="text-text-muted">
              {{ log.credits_used.toLocaleString() }}
            </BaseText></td>
            <!-- Log Details Button -->
            <td>
              <div v-if="log.has_trace_data">
                <BaseLoadingSpinnerCircle v-if="loadingLogDetails === log.trace_id" :duration="0.9" class="text-icon-normal" />
                <button v-if="loadingLogDetails !== log.trace_id" class="w-full h-full flex items-center justify-center text-icon-normal transition-colors duration-100 group-hover:text-icon-muted">
                  <svg v-if="selectedLog?.trace_id === log.trace_id" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" class="fade-in">
                    <path d="M10 16L6.70711 12.7071C6.31658 12.3166 6.31658 11.6834 6.70711 11.2929L10 8M17 16L13.7071 12.7071C13.3166 12.3166 13.3166 11.6834 13.7071 11.2929L17 8" 
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-if="selectedLog?.trace_id !== log.trace_id" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="fade-in">
                    <path d="M11.042 3.95837H3.95866C3.03818 3.95837 2.29199 4.70457 2.29199 5.62504V14.375C2.29199 15.2955 3.03818 16.0417 3.95866 16.0417H11.042M13.5381 3.95837H16.0381C16.9586 3.95837 17.7048 4.70457 17.7048 5.62504V14.375C17.7048 15.2955 16.9586 16.0417 16.0381 16.0417H13.5381M13.5381 3.95837V2.08337M13.5381 3.95837V16.0417M13.5381 16.0417V17.9167M7.08366 7.91671L9.16699 10L7.08366 12.0834" 
                    stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- Load More Button -->
      <div v-if="nextId && !loadingLogs && logs?.length" class="sticky h-10 bottom-0 left-0 right-0 flex items-center justify-center transition-colors duration-100" 
      :class="[loadingMoreLogs ? 'text-text-disabled cursor-default' : 'text-text-normal hover:text-text-muted cursor-pointer', { 'border-b border-border-normal': tableHasXOverflow }]"
      @click="getMoreLogs">
        <div class="flex items-center gap-1.5 transition-opacity duration-75" :class="hideLoadMore ? 'opacity-0' : 'opacity-100'">
          <BaseLoadingSpinnerCircle v-if="loadingMoreLogs" :duration="0.9" />
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M18.25 14.8285L13.4142 19.6643C12.6332 20.4453 11.3669 20.4453 10.5858 19.6643L5.75 14.8285M12 19.3285L12 3.82849" 
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <BaseText type="label" size="sm">
            Load More
          </BaseText>
        </div>
      </div>
    </div>
    <!-- LOG REQUEST DETAILS -->
    <transition name="details-spacer">
      <div v-show="selectedLog && logDetails" class="details-spacer flex-shrink-0 flex justify-end" 
      :style="{ '--details-width': `${detailsSpacerWidth}px` }">
        <div ref="detailsContent" class="details-spacer-content flex-shrink-0" :class="{'simple-enter': !doAnimDetailsContentTranslate}">
          <transition name="fade">
            <LogRequestDetails 
              v-if="selectedLog && logDetails" 
              ref="logDetails" 
              :log="selectedLog" 
              :logDetails="logDetails"
              :detailsMaximized="detailsMaximized"
              :detailsBaseWidth="detailsSpacerWidth - 20"
              :detailsAnimDur="DETAILS_ANIM_DUR"
              :loadingLogDetails="!!(loadingLogDetails)"
              @toggleDetailsMaximized="toggleDetailsMaximized"
            />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import { getCountryName } from '../../utils/countryUtils'
import { ceilDate } from '../../utils/dateUtils'
import ForeplayAPI from '@/api/foreplayServer'
import smoothReflow from 'vue-smooth-reflow'

// Components
import LogsFilterToolbar from './logs/LogsFilterToolbar.vue'
import LogRequestDetails from './logs/LogRequestDetails.vue'

// Icons
import NoSearchResultsIcon from '../globals/Icons/SidebarIcons/NoSearchResultsIcon.vue'

const LOG_LIMIT = 40 // The max number of logs per pagination request
const TABLE_MAX_WIDTH = 1400 // The max width of the logs table (px)
const DETAILS_ANIM_DUR = 300 // The duration (ms) of the log request details animations

const MAX_TRACE_LOOKBACK_DAYS = 60

export default {
  name: 'ApiLogs',
  mixins: [smoothReflow],
  components: {
    LogsFilterToolbar,
    LogRequestDetails,
    NoSearchResultsIcon
  },
  props: {
    disableScroll: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // Data States
      logs: [],
      nextId: null,
      selectedLog: null,
      logDetails: null,

      // Filter States
      queryFields: {
        ip_address: null,
        endpoint: null,
        method: null,
        status_code: null,
        country: null
      },
      selectedDateRange: {
        start: null,
        end: null,
        isRelativeToToday: false
      },
      sortDirection: 'newest',

      // UI States
      detailsMaximized: false,
      initPosition: { top: 0, left: 0, bottom: 0, right: 0 },
      endPosition: { top: 0, left: 0, bottom: 0, right: 0 },
      tableHasXOverflow: false,
      DETAILS_ANIM_DUR,
      TABLE_MAX_WIDTH,

      loadingLogs: false,
      loadingLogsRefetch: false,
      loadingMoreLogs: false,
      loadingLogDetails: null,
      logsContentWidth: 0,
      contentResizeObserver: null,
      tableResizeObserver: null,
      hideLoadMore: false,
    }
  },
  watch: {
    logFilters: {
      handler () {
        this.fetchLogs()
      },
      deep: true
    },
    getSidebarCollapsed (isCollapsed) {
      if (this.detailsMaximized) this.handleSidebarCollapseTransition(isCollapsed)
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getSidebarCollapsed', 'getSidebarCollapseDuration']),
    formattedLogs () {
      return this.logs.map(log => ({
        ...log,
        timestamp: moment.utc(log.timestamp).local().format('YYYY-MM-DD HH:mm:ss'),
        country: getCountryName(log.country),
        product: this.getProductName(log.endpoint),
        latency_ms: `${log.latency_ms}ms`,
        endpoint: log.endpoint.replace('/api', ''),
        full_endpoint: log.endpoint,

        // We no longer store the trace data for older logs
        has_trace_data: moment(log.timestamp).isAfter(moment().subtract(MAX_TRACE_LOOKBACK_DAYS - 1, 'days'))
      }))
    },
    logFilters () {
      const { start: startDate, end: endDate } = this.selectedDateRange
      return {
        order_direction: this.sortDirection,
        ...Object.fromEntries(Object.entries(this.queryFields).filter(([_, value]) => value)),
        ...(startDate && { start_date: moment(ceilDate(startDate)).utc().format('YYYY-MM-DDTHH:mm:ss') }),
        ...(endDate   && { end_date: moment(ceilDate(endDate)).utc().format('YYYY-MM-DDTHH:mm:ss') })
      }
    },
    detailsSpacerWidth () {
      return Math.min(this.logsContentWidth * 0.9 / 2, 864)
    },
    doAnimDetailsContentTranslate () {
      // Only animate the details content x translate if there are more than 15 logs
      // The animation looks jarring when the table is not filled out
      return this.logs?.length > 15
    },
    showTableBottomBorder () {
      return this.tableHasXOverflow || this.nextId || !this.logs?.length
    }
  },
  async mounted () {
    this.fetchLogs(true)

    // UI Setup
    this.$nextTick(() => {
      // Add resize observer to logs content (used to calculate details spacer width)
      this.logsContentWidth = this.$refs.logsContent.clientWidth
      this.contentResizeObserver = new ResizeObserver(entries => {
        this.logsContentWidth = entries[0].contentRect.width
      })
      this.contentResizeObserver.observe(this.$refs.logsContent)

      // Add resize observer to logs table (used to determine if the table has x-overflow)
      this.tableHasXOverflow = this.$refs.logsTable.scrollWidth > this.$refs.logsTable.clientWidth
      this.tableResizeObserver = new ResizeObserver(entries => {
        const { target } = entries[0]
        this.tableHasXOverflow = target.scrollWidth > target.clientWidth
      })
      this.tableResizeObserver.observe(this.$refs.logsTable)

      requestAnimationFrame(() => {
        this.$smoothReflow({
          el: this.$refs.logsTable,
          property: 'height',
          transition: 'height 200ms cubic-bezier(.85,0,.15,1)'
        })
      })
    })
  },
  beforeDestroy () {
    this.contentResizeObserver?.disconnect()
    this.tableResizeObserver?.disconnect()
    this.$emit('update:disable-scroll', false)
    if (this.$refs.logsTable) this.$unsmoothReflow({ el: this.$refs.logsTable })
  },
  methods: {
    async fetchLogs (isInitFetch = false) {
      if (!isInitFetch) {
        this.loadingLogsRefetch = true
        await this.closeAllDetails()
      } else {
        this.loadingLogs = true
      }
      try {
        const pagination = {
          limit: LOG_LIMIT
        }
        const { metadata, data } = await ForeplayAPI.PublicAPI.getLogs(pagination, this.logFilters)
        this.logs = data.operations
        this.nextId = data.operations.length >= LOG_LIMIT ? metadata.cursor : null
        this.shutterLoadMore()
      } catch (error) {
        const status = error?.response?.status
        const payload = error?.response?.data
        if (status === 400 && payload?.error?.error) {
          const { message, error: detail } = payload.error.error
          this.$showAlert({
            type: 'error',
            message: `${message}: ${detail}`
          })
          this.clearQueryFields()
        } else {
          console.error(error)
          this.$showAlert({
            type: 'error',
            message: 'Failed to fetch logs. See console for details',
          })
        }
      } finally {
        this.loadingLogs = false
        this.loadingLogsRefetch = false
      }
    },
    async getMoreLogs () {
      if (this.loadingMoreLogs) return
      this.loadingMoreLogs = true
      try {
        const pagination = {
          limit: LOG_LIMIT,
          cursor: this.nextId
        }
        const { metadata, data } = await ForeplayAPI.PublicAPI.getLogs(pagination, this.logFilters)
        this.logs = [...this.logs, ...data.operations]
        this.nextId = data.operations.length >= LOG_LIMIT ? metadata.cursor : null
        this.shutterLoadMore()
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingMoreLogs = false
      }
    },
    selectLog (log) {
      if (!log.has_trace_data) return
      if (this.selectedLog?.trace_id === log.trace_id) {
        // If we're closing details, we need to optimize the log details for exit
        this.$refs.logDetails?.optimizeForExit()
        this.$nextTick(() => { 
          this.selectedLog = null 
          this.logDetails = null
        })
      } else {
        this.selectedLog = log
        this.fetchLogDetails(log.trace_id)
      }
    },
    async fetchLogDetails (traceId) {
      this.loadingLogDetails = traceId
      try {
        const { data } = await ForeplayAPI.PublicAPI.getRequestDetails(traceId)
        this.logDetails = data?.[0] ?? data
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to fetch log details. See console for details',
        })
      } finally {
        this.loadingLogDetails = null
      }
    },
    clearQueryFields () {
      this.queryFields = {
        ip_address: null,
        endpoint: null,
        method: null,
        status_code: null,
        country: null
      }
    },

    // ================================ UI Methods ================================
    isLogRowActive (log) {
      return this.selectedLog?.trace_id === log.trace_id
    },
    getProductName (endpoint) {
      const productGroup = endpoint?.split('/')?.[2] ?? 'Unknown'
      let productName = productGroup
      switch (productGroup) {
        case 'ad': productName = 'Ad Library'; break;
        case 'spyder': productName = 'Spyder'; break;
        case 'board':
        case 'boards': productName = 'Swipe File'; break;
        case 'brand': productName = 'Brand Library'; break;
        case 'discovery': productName = 'Discovery'; break;
      }
      return productName
    },
    shutterLoadMore () {
      this.hideLoadMore = true
      setTimeout(() => { this.hideLoadMore = false }, 200)
    },
    toggleDetailsMaximized (handleOptimize = true) {
      const contentEl = this.$refs.detailsContent
      const isExpanding = !this.detailsMaximized
      let animation
      if (handleOptimize) this.$refs.logDetails?.optimizeForAnimation()
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          if (isExpanding) {
            this.$emit('update:disable-scroll', true)
            const contentRect = contentEl.getBoundingClientRect()
            const filterBarRect = document.getElementById('logs-filter-bar').getBoundingClientRect()

            // Set the initial position variables
            this.initPosition = {
              top: `${contentRect.top}px`,
              left: `${contentRect.left}px`,
              bottom: `${window.innerHeight - contentRect.bottom}px`,
              right: `${window.innerWidth - contentRect.right}px`
            }
            const sidebarWidth = this.getSidebarWidth()
            this.endPosition = {
              top: `${filterBarRect.bottom + 17}px`,
              left: `${sidebarWidth + 16}px`,
              bottom: `32px`,
              right: `32px`
            }

            contentEl.classList.add('animating')
            animation = contentEl.animate(
              [this.initPosition, this.endPosition],
              { duration: DETAILS_ANIM_DUR, easing: 'ease-in-out', fill: 'forwards' }
            )
          } else {
            animation = contentEl.animate(
              [this.endPosition, this.initPosition],
              { duration: DETAILS_ANIM_DUR, easing: 'ease-in-out', fill: 'forwards' }
            )
          }
          animation.onfinish = () => {
            if (!isExpanding) {
              contentEl.classList.remove('animating')
              animation.cancel()
              this.$emit('update:disable-scroll', false)
            }
            if (handleOptimize) setTimeout(() => { this.$refs.logDetails?.handleAnimationEnd() }, 50)
          }
          this.detailsMaximized = !this.detailsMaximized
        })
      })
    },
    handleSidebarCollapseTransition (isCollapsing) {
      if (!this.detailsMaximized) return
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$emit('update:disable-scroll', true)
          const contentEl = this.$refs.detailsContent
          if (!contentEl) return
          const sidebarWidth = this.getSidebarWidth(isCollapsing)

          const initPos = { ...this.endPosition }
          const endPos = {
            ...this.endPosition,
            left: `${sidebarWidth + 16}px`
          }
          this.endPosition = endPos

          contentEl.classList.add('animating')
          contentEl.animate(
            [initPos, endPos],
            { duration: this.getSidebarCollapseDuration, easing: 'ease-in-out', fill: 'forwards' }
          )
        })
      })
    },
    async closeAllDetails () {
      if (!this.detailsMaximized && !this.selectedLog) return
      this.$refs.logDetails?.optimizeForExit()
      await this.$nextTick()
      if (this.detailsMaximized) {
        this.toggleDetailsMaximized(false)
        await new Promise(resolve => setTimeout(resolve, DETAILS_ANIM_DUR))
      }
      if (this.selectedLog) {
        this.selectedLog = null
        this.logDetails = null
        await new Promise(resolve => setTimeout(resolve, DETAILS_ANIM_DUR))
      }
    },
    getSidebarWidth (isCollapsed = null) {
      const collapsed = (isCollapsed === null ? this.getSidebarCollapsed : isCollapsed)
      return collapsed ? 76 : 288
    }
  }
}
</script>

<style scoped>
.colored-label {
  padding: 4px 5px 4px 5px;
  border-radius: 4px;

  font-family: Inter;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px; /* 109.091% */
}
.colored-label.success, .colored-label.POST {
  background-color: #D1F4EE;
  color: #00826E;
}
.colored-label.error {
  background-color: #FFF6E0;
  color: #603A20;
}
.colored-label.GET {
  background-color: #CCE5FF;
  color: #003E6B;
}
.colored-label.DELETE {
  background-color: #FFD7E0;
  color: #A40027;
}

.scroll-container {
  position: relative;
  max-width: var(--table-max-width);
  height: max-content;
  border-radius: 8px;
  scroll-behavior: smooth;
}
.scroll-container::-webkit-scrollbar {
  height: 12px;
}
.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(236, 239, 243, 1);
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
  transition: border-width 150ms ease-in-out;
}
.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(223, 225, 231, 1);
  border-width: 2px;
}

.scroll-container.anim-shrink {
  animation: shrink var(--details-anim-dur) ease-in-out forwards;
}
.scroll-container.anim-expand {
  animation: expand var(--details-anim-dur) ease-in-out forwards;
}
@keyframes shrink {
  from {
    transform: perspective(1000px) translate3d(0, 0, 0);
    opacity: 1;
  }
  to {
    transform: perspective(1000px) translate3d(0, 0, -70px);
    opacity: 0;
  }
}
@keyframes expand {
  from {
    transform: perspective(1000px) translate3d(0, 0, -70px);
    opacity: 0;
  }
  to {
    transform: perspective(1000px) translate3d(0, 0, 0);
    opacity: 1;
  }
}

/* Log Details Styles */
.details-spacer {
  position: relative;
  width: var(--details-width);
  /* border: 1px solid red; */
}
.details-spacer-enter-active, .details-spacer-leave-active {
  transition: width var(--details-anim-dur) ease-in-out;
}
.details-spacer-enter-from, .details-spacer-enter, .details-spacer-leave-to {
  width: 0px;
}
.details-spacer-enter-to, .details-spacer-leave {
  width: var(--details-width);
}
.details-spacer-content {
  position: sticky;
  top: 127px;
  height: calc(100vh - 221px);
  width: calc(var(--details-width) - 20px);
  /* transition: height 10ms ease-in-out; */
  border-radius: 8px;
  border: 1px solid #ECEFF3;
  background-color: white;
}

.details-spacer-content.animating {
  position: fixed;
  z-index: 30000;
  width: auto;
  height: auto;
}

.details-spacer-enter-active .details-spacer-content, .details-spacer-leave-active .details-spacer-content {
  transition: transform var(--details-anim-dur) ease-in-out, opacity var(--details-anim-dur) ease-in-out;
}
.details-spacer-enter-from .details-spacer-content, .details-spacer-enter .details-spacer-content, .details-spacer-leave-to .details-spacer-content {
  transform: translateX(-150px) scale(0.9);
  opacity: 0.25;
}
.details-spacer-enter-to .details-spacer-content, .details-spacer-leave .details-spacer-content {
  transform: translateX(0px) scale(1);
  opacity: 1;
}
.details-spacer-enter-from .details-spacer-content.simple-enter, 
.details-spacer-enter .details-spacer-content.simple-enter, 
.details-spacer-leave-to .details-spacer-content.simple-enter {
  transform: scale(0.9);
}
.details-spacer-enter-to .details-spacer-content.simple-enter, .details-spacer-leave .details-spacer-content.simple-enter {
  transform: scale(1);
}
.fade-enter-active, .fade-leave-active {
  transition: opacity var(--details-anim-dur) ease-in-out;
}
.fade-enter-from, .fade-enter, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave {
  opacity: 1;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave {
  opacity: 1;
}

/* Table Styles */
table {
  border-collapse: separate;
  border-spacing: 0px;
  width: 100%;
}
.header-row {
  background-color: white;
}
.header-row th {
  padding: 8px 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  border-bottom: 1px solid #ECEFF3;
}
.header-row th:first-child {
  position: sticky;
  left: 0;
  border-right: 1px solid #ECEFF3;
  min-width: 176px;
  z-index: 3;
  background-color: white;
}
.header-row th:not(:first-child):not(:last-child) {
  min-width: 126px;
  text-align: left;
}
.header-row th:not(:first-child):not(:last-child):not(:nth-last-child(2)) {
  border-right: 1px solid #ECEFF3;
}
.header-row th:last-child {
  position: sticky;
  right: 0;
  border-left: 1px solid #ECEFF3;
  min-width: 44px;
  height: 40px;
  z-index: 3;
  background-color: white;
}

.log-row {
  transition: background-color 100ms ease-in-out;
  background-color: white;
}
.log-row:not(.no-trace-data) {
  cursor: pointer;
}
.log-row:hover:not(.no-trace-data), .log-row:hover:not(.no-trace-data) td:first-child, .log-row:hover:not(.no-trace-data) td:last-child {
  background-color: #fbfcfd;
}
.log-row.active:not(.no-trace-data), .log-row.active:not(.no-trace-data) td:first-child, .log-row.active:not(.no-trace-data) td:last-child {
  background-color: #F6F8FA;
}
.log-row td {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.log-row td:first-child {
  position: sticky;
  left: 0;
  padding: 8px 12px 8px 12px;
  border-right: 1px solid #ECEFF3;
  border-bottom: 1px solid #ECEFF3;
  z-index: 3;
  transition: background-color 100ms ease-in-out;
  background-color: white;
}
.log-row td:not(:first-child):not(:last-child) {
  border-bottom: 1px solid #ECEFF3;
  padding: 8px 12px 8px 12px;
}
.log-row td:not(:first-child):not(:last-child):not(:nth-last-child(2)) {
  border-right: 1px solid #ECEFF3;
}
.log-row td:last-child {
  position: sticky;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #ECEFF3;
  border-left: 1px solid #ECEFF3;
  width: 44px;
  height: 40px;
  z-index: 3;
  transition: background-color 100ms ease-in-out;
  background-color: white;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>